import { useMemo } from 'react';

import { pluralize } from '@/react/common/string-utils';

import { PortainerSelect } from '@@/form-components/PortainerSelect';

interface NamespaceSelectProps {
  selectedNamespaces: string[];
  onChange(value: string[]): void;
  placeholder?: string;
  'data-cy': string;
  namespaces: string[];
}

export function NamespaceSelect({
  selectedNamespaces,
  onChange,
  placeholder = 'e.g. default, kube-system, production',
  'data-cy': dataCy,
  namespaces,
}: NamespaceSelectProps) {
  const namespaceOptions = useMemo(() => {
    const namespaceOptions = Array.from(
      new Set([...namespaces, ...selectedNamespaces])
    ).map((v) => ({
      label: v,
      value: v,
    }));
    // If there are 2 or more namespaces in the list, show the "Select all" option
    if (namespaces.length - selectedNamespaces.length >= 2) {
      return [{ label: 'Select all', value: 'all' }, ...namespaceOptions];
    }
    return namespaceOptions;
  }, [namespaces, selectedNamespaces]);

  return (
    <PortainerSelect<string>
      isMulti
      isCreatable
      value={selectedNamespaces || []}
      onChange={(selectedNamespaces) => {
        if (selectedNamespaces.some((namespace) => namespace === 'all')) {
          onChange(namespaces);
        } else {
          onChange(selectedNamespaces.map((ns) => ns.trim()));
        }
      }}
      options={namespaceOptions}
      formatCreateLabel={(input) => {
        const hasDelimiters = /[,\s]/.test(input);
        if (hasDelimiters) {
          const namespaces = input
            .split(/[,\s]+/)
            .map((ns) => ns.trim())
            .filter(Boolean);
          const count = namespaces.length;
          return `Add ${count} ${pluralize(
            count,
            'namespace'
          )}: "${namespaces.join(', ')}"`;
        }
        return `Add namespace "${input}". \nAdd multiple namespaces by separating them with commas or spaces.`;
      }}
      onCreateOption={handleCreateOption}
      placeholder={placeholder}
      bindToBody
      data-cy={dataCy}
      noOptionsMessage={() =>
        'Type to add namespaces. You can add multiple namespaces by separating them with commas or spaces.'
      }
    />
  );

  function handleCreateOption(newNamespace: string) {
    const trimmed = newNamespace.trim();
    if (!trimmed) {
      return;
    }

    // Split by commas, spaces, or both and filter out empty strings
    const namespaceList = trimmed
      .split(/[,\s]+/)
      .map((ns) => ns.trim())
      .filter(Boolean);

    // Add all non-empty namespaces
    if (namespaceList.length > 0) {
      const uniqueNamespaces = Array.from(
        new Set([...selectedNamespaces, ...namespaceList])
      );
      onChange(uniqueNamespaces);
    }
  }
}
